import { memo, type ReactElement } from 'react';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useFeaturesTableController } from '@/features/features/components/useFeaturesTableController';
import {
  TABLE_ROW_HEIGHT,
  TABLE_ROW_MEASURE_HEIGHT,
} from '@/features/shared/components/tableSystem/tableConstants';
import { tableSx } from '@/features/shared/components/tableSystem/tableSx';
import { getDialogMessage } from '@/shared/copy/dialogs';
import { getEmptyStateMessage } from '@/shared/copy/emptyStates';
import { ServerDataTable } from '@/ui/composites/ServerDataTable';
import type { FeaturesTableProps } from '@/features/features/components/FeaturesTable.types';
import type { FeatureDto } from '@flowqa/shared';

const FeaturesTableComponent = ({
  persistKey,
  syncStateToUrl = false,
  pageSizeOptions = [5, 10, 25, 50],
  initialPageSize = 5,
  initialData,
  disablePageSizeSelect,
  noRowsText = getEmptyStateMessage('entity.noneFound', { entity: 'feature' }),
  fixedFilters,
  initialSortModel,
  initialFilterModel,
  queryFn,
  queryKey,
  onRowsChange,
  onQueryInput,
  onDataUpdatedAt,
  showLastUpdated,
  columnVisibilityModel,
  onColumnVisibilityModelChange,
}: FeaturesTableProps): ReactElement => {
  const {
    columns,
    deleteLoading,
    deleteTarget,
    gridConfig,
    isAuthenticated,
    onDeleteClose,
    onDeleteConfirm,
    queryFn: resolvedQueryFn,
    queryKey: resolvedQueryKey,
    selectedOrganisationId,
    selectedProjectId,
  } = useFeaturesTableController({
    fixedFilters,
    initialSortModel,
    initialFilterModel,
    persistKey,
    queryFn,
    queryKey,
    syncStateToUrl,
  });

  return (
    <>
      <ServerDataTable<FeatureDto>
        queryPath="features/query"
        {...gridConfig}
        initialData={initialData}
        queryFn={resolvedQueryFn}
        queryKey={resolvedQueryKey}
        columns={columns}
        getRowId={(row) => row.id}
        isAuthenticated={isAuthenticated}
        organisationId={selectedOrganisationId}
        projectId={selectedProjectId}
        pageSizeOptions={pageSizeOptions}
        initialPageSize={initialPageSize}
        disablePageSizeSelect={disablePageSizeSelect}
        noRowsText={noRowsText}
        interactionMode="static"
        getRowHeight={() => 'auto'}
        skeletonRowHeight={TABLE_ROW_HEIGHT}
        skeletonGetRowHeight={null}
        showLastUpdated={showLastUpdated}
        autoHeight
        onRowsChange={onRowsChange}
        onQueryInput={onQueryInput}
        onDataUpdatedAt={onDataUpdatedAt}
        columnVisibilityModel={columnVisibilityModel}
        showColumnVerticalBorder={false}
        showCellVerticalBorder={false}
        gridProps={{
          getEstimatedRowHeight: () => TABLE_ROW_MEASURE_HEIGHT,
          onColumnVisibilityModelChange,
        }}
        sx={[
          ...(Array.isArray(tableSx) ? tableSx : [tableSx]),
          {
            '& .MuiDataGrid-cell[data-field="name"], & .MuiDataGrid-cell[data-field="status"], & .MuiDataGrid-cell[data-field="suiteLatestRunPassed"], & .MuiDataGrid-cell[data-field="testLatestRunPassed"], & .MuiDataGrid-cell[data-field="actions"]':
              {
                alignItems: 'flex-start',
              },
          },
        ]}
      />
      <ConfirmDialog
        open={deleteTarget !== null}
        title={getDialogMessage('destructive.delete.title', { entity: 'feature' })}
        description={getDialogMessage('destructive.delete.simpleDescription', {
          entity: 'feature',
        })}
        confirmLabel={getDialogMessage('destructive.delete.confirmLabel', {
          entity: 'feature',
        })}
        confirmColor="error"
        loading={deleteLoading}
        onClose={onDeleteClose}
        onConfirm={onDeleteConfirm}
      />
    </>
  );
};

export const FeaturesTable = memo(FeaturesTableComponent);
